<script lang="ts">
	import type { LoadResult } from '@kestrel/server';
	import type { Unset } from '@otto-ec/global-resources/nexus';
	import { mount, onDestroy, onMount } from 'svelte';
	import { logger } from '../../logger.ts';
	import type { load } from './PointsInfoBannerServer.ts';
	import PointsInfoBanner from '../../components/points-info-banner/PointsInfoBanner.svelte';
	import type BenefitToggledEvent from '../../domain/points-info-banner/BenefitToggledEvent.ts';
	import { PointsInfoBannerType } from '../../domain/points-info-banner/PointsInfoBannerType.ts';
	import {
		addElementToPointsInfoBannerContextStore,
		pointsInfoBannerContextStore
	} from '../../store/PointsInfoBannerContextStore.ts';
	import type VariationChangedEvent from '../../domain/VariationChangedEvent.ts';

	let { data }: { data: LoadResult<typeof load> } = $props();
	let targetElement: HTMLElement;
	let isVariationChangeHandlerFinish: boolean = false;
	let handleVariationChangeRef: Unset;

	const CHECK_FREQUENCY = 100;
	const TIMEOUT = 4000;

	async function fetchContext(variationId: string, benefitId: string | undefined = undefined) {
		let url = `/up-teaserui/gateway/pointsInfoBannerContext?variationId=${variationId}`;
		if (benefitId !== undefined) {
			url += `&benefitId=${benefitId}`;
		}

		const cachedContextResponse = $pointsInfoBannerContextStore.get(url);
		if(cachedContextResponse){
			logger.info('Response from cache for pointsInfoBannerContext with pointsInfoBannerType: ' + cachedContextResponse.pointsInfoBannerType);
			return cachedContextResponse;
		}

		try {
			let response = await fetch(url, {
				credentials: 'include',
				method: 'GET',
				headers: { 'Content-Type': 'application/json' }
			});
			if (response.status === 200) {
				return response.json().then(response => {
					logger.info('Response status 200 from pointsInfoBannerContext with pointsInfoBannerType: ' + response.pointsInfoBannerType);
					addElementToPointsInfoBannerContextStore(url, response);
					return {
						pointsInfoBannerContent: response.pointsInfoBannerContent,
						pointsInfoBannerType: response.pointsInfoBannerType
					};
				});
			} else {
				logger.error(`Status ${response.status} updating points info banner context`);
				return undefined;
			}
		} catch (error) {
			logger.error('Error updating points info banner context: ' + error);
			return undefined;
		}
	}

	const handleVariationChangedEvent = async (event: VariationChangedEvent) => {
		logger.info(`Handling variation changed event with variation ${event.variationId}`);

		if(event.cdc){
			event.targetElement.innerHTML = event.variationId;
		}else {
			const response = await fetchContext(event.variationId);

			if (!response || !event.targetElement || response.pointsInfoBannerType === PointsInfoBannerType.NO_CONTENT) {
				return;
			}

			targetElement = event.targetElement;

			mount(PointsInfoBanner, {
				target: event.targetElement,
				props: {
					content: response.pointsInfoBannerContent,
					type: response.pointsInfoBannerType
				}
			});
		}
		isVariationChangeHandlerFinish = true;
	};

	const handleBenefitToggleEvent = async (event: BenefitToggledEvent) => {
		const { variationId, id: benefitId } = event;
		let isBenefitChangeHandlerInProgress = false;

		let intervalId = setInterval(async ()=> {
			if(isVariationChangeHandlerFinish && !isBenefitChangeHandlerInProgress){
				isBenefitChangeHandlerInProgress = true;
				await handleBenefitChange(intervalId, variationId, benefitId);
				isBenefitChangeHandlerInProgress = false;
			}
		}, CHECK_FREQUENCY);

		setTimeout(()=> {
			clearInterval(intervalId);
		}, TIMEOUT);
	}

	const handleBenefitChange = async (intervalId: any, variationId: string, benefitId: string) => {
		logger.info(`Handling benefit toggled event with variation ${variationId} and benefit ${benefitId}`);
		const response = await fetchContext(variationId, benefitId);

		if (!response || !targetElement || response.pointsInfoBannerType === PointsInfoBannerType.NO_CONTENT) {
			return;
		}

		targetElement.innerHTML = '';

		mount(PointsInfoBanner, {
			target: targetElement,
			props: {
				content: response.pointsInfoBannerContent,
				type: response.pointsInfoBannerType
			}
		});
		isVariationChangeHandlerFinish = false;
		clearInterval(intervalId);
	};

	onMount(async () => {
		if (data.disablePointsInfoBanner) {
			return;
		}
		const { benefitEvents } = await import ('@otto-ec/nexus-registry/benefit/events');
		const { pointsTeaserVariationChange } = await import ('@otto-ec/nexus-registry/promo/up/teaser');

		benefitEvents.active.on(handleBenefitToggleEvent);

		window.o_global.eventQBus.on('ft9.up-teaser-info-banner.variationChanged', handleVariationChangedEvent);
		if(data.isDevelop){
			handleVariationChangeRef = pointsTeaserVariationChange.assignFunction(handleVariationChangedEvent);
		}
	});

	onDestroy(()=>{
		if(handleVariationChangeRef) {
			handleVariationChangeRef();
		}
	})
</script>
